// MetaSettingsCard — Settings ページ内の Meta / Instagram 接続カード (2026-08-03)
//
// 設計:
// - GA4 カード風 + 日本語ネイティブ文言
// - 5 状態:未接続 / OAuth 設定欠如 / 連携中 / 連携済 / 再認証要 / 多候補選択
// - backend: GET /api/v1/settings/meta
// - OAuth 入口: GET /api/v1/settings/meta/oauth-start → 302 → Facebook
// - URL query (?meta=connected|candidates|no_accounts|failed) を読み取り inline 状態表示
// - 断開+本地データ削除: DELETE /api/v1/settings/meta/connection/{id}/purge

import { type ReactNode, useCallback, useEffect, useState } from "react";
import {
  CheckCircle2, AlertCircle, ExternalLink, RefreshCw, Trash2,
  Instagram, Loader2, Info, X, Send, MessageCircle, Inbox, Link2, Building2,
  Flag, Database, KeyRound,
} from "lucide-react";
import { Badge } from "../../components/ui/Badge";
import { Button } from "../../components/ui/Button";
import { ApiError } from "../../api/client";
import {
  metaSettingsApi,
  type MetaConnection,
  type MetaSettingsResponse,
} from "../../api/metaSettings";
import { useUiLanguage } from "../../i18n/UiLanguageProvider";

type ViewState =
  | { kind: "loading" }
  | { kind: "unconfigured" }
  | { kind: "no_connection"; configured: boolean }
  | { kind: "connected"; connection: MetaConnection; configured: boolean }
  | { kind: "reauth_required"; connection: MetaConnection; configured: boolean; message: string }
  | { kind: "error"; message: string; configured: boolean };

type InfoPanel = "permissions" | "disconnect" | null;

const OAUTH_RESULT_MESSAGES = {
  connected: [
    "Meta / Instagram アカウントとの連携が完了しました。",
    "Meta / Instagram account connection is complete.",
  ],
  candidates: [
    "連携可能な Instagram プロアカウントが見つかりました。アカウントを選択してください。",
    "Available Instagram professional accounts were found. Select an account to connect.",
  ],
  no_accounts: [
    "連携可能な Instagram プロアカウントが見つかりませんでした。Meta Business Suite で Facebook ページと Instagram プロアカウントの連携をご確認ください。",
    "No connectable Instagram professional account was found. Check the Facebook Page and Instagram professional account connection in Meta Business Suite.",
  ],
  failed: [
    "Meta 連携に失敗しました。",
    "Meta connection failed.",
  ],
} as const;

function formatDateTime(iso: string | null | undefined, language: "ja" | "en"): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(language === "en" ? "en-US" : "ja-JP", { timeZone: "Asia/Tokyo" });
  } catch {
    return iso;
  }
}

function formatScopes(scopes: string[] | null | undefined): string {
  if (!scopes || scopes.length === 0) return "—";
  return scopes.join(" / ");
}

export function MetaSettingsCardInner() {
  const { language, pick } = useUiLanguage();
  const [view, setView] = useState<ViewState>({ kind: "loading" });
  const [busy, setBusy] = useState<"load" | "purge" | null>(null);
  const [oauthResultMsg, setOauthResultMsg] = useState<string | null>(null);
  const [confirmDisconnectAndPurge, setConfirmDisconnectAndPurge] = useState(false);
  const [retryTick, setRetryTick] = useState(0);
  const [infoPanel, setInfoPanel] = useState<InfoPanel>(null);

  // OAuth callback 后 URL query 读取
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const status = params.get("meta");
    if (status && status in OAUTH_RESULT_MESSAGES) {
      const message = OAUTH_RESULT_MESSAGES[status as keyof typeof OAUTH_RESULT_MESSAGES];
      setOauthResultMsg(pick(message[0], message[1]));
      params.delete("meta");
      const qs = params.toString();
      const url = window.location.pathname + (qs ? "?" + qs : "");
      window.history.replaceState({}, "", url);
    }
  }, [pick]);

  const load = useCallback(async () => {
    setBusy("load");
    try {
      const data: MetaSettingsResponse = await metaSettingsApi.getSettings();
      if (!data.configured) {
        setView({ kind: "unconfigured" });
      } else if (!data.connections || data.connections.length === 0) {
        setView({ kind: "no_connection", configured: data.configured });
      } else {
        const primary =
          data.connections.find((c) => c.is_active && c.status === "connected") ||
          data.connections[0];
        if (primary.status === "connected") {
          setView({ kind: "connected", connection: primary, configured: data.configured });
        } else if (primary.status === "reauth_required") {
          setView({
            kind: "reauth_required",
            connection: primary,
            configured: data.configured,
            message: primary.last_error_message || pick("再認証が必要です。", "Reauthentication is required."),
          });
        } else {
          setView({ kind: "no_connection", configured: data.configured });
        }
      }
    } catch (e) {
      const isUnauthorized = e instanceof ApiError && e.status === 401;
      if (isUnauthorized) {
        setView({
          kind: "error",
          message:
            pick(
              "セッションの有効期限が切れました。ページを再読み込みしてログインし直してください。",
              "Your session has expired. Reload the page and sign in again.",
            ),
          configured: false,
        });
      } else {
        const status = e instanceof ApiError ? ` [HTTP ${e.status}]` : "";
        const code = e instanceof ApiError && e.code ? ` (${e.code})` : "";
        const msg =
          (e instanceof ApiError ? e.message : pick("設定情報の取得に失敗しました。", "Failed to load settings.")) +
          status +
          code;
        setView({ kind: "error", message: msg, configured: false });
      }
    } finally {
      setBusy(null);
    }
  }, [pick]);

  useEffect(() => {
    load();
  }, [load, retryTick]);

  const handleConnect = useCallback(() => {
    window.location.href = "/api/v1/settings/meta/oauth-start?next=/settings";
  }, []);

  const handleReconnect = useCallback(() => {
    window.location.href = "/api/v1/settings/meta/oauth-start?next=/settings";
  }, []);

  const handleDisconnectAndPurge = useCallback(async () => {
    if (view.kind !== "connected" && view.kind !== "reauth_required") return;
    setBusy("purge");
    try {
      await metaSettingsApi.purgeLocalData(view.connection.id);
      setConfirmDisconnectAndPurge(false);
      await load();
      setOauthResultMsg(pick(
        "Instagram 連携を解除し、ローカルの Instagram データを削除しました。Instagram 上の実データは削除していません。",
        "Instagram was disconnected and local Instagram data was deleted. No live Instagram data was deleted.",
      ));
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : pick("連携解除とローカルデータ削除に失敗しました。", "Failed to disconnect and delete local data.");
      setView({ kind: "error", message: msg, configured: view.configured });
    } finally {
      setBusy(null);
    }
  }, [view, load, pick]);

  const handleRetry = useCallback(() => {
    // 强制 hard reload 页面 — 保证最新 bundle
    window.location.reload();
  }, []);

	  return (
	    <div className="space-y-4">
      {oauthResultMsg && (
        <div className="rounded-md border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-900 flex items-start gap-2">
          <CheckCircle2 size={16} className="mt-0.5 flex-shrink-0" />
          <div>{oauthResultMsg}</div>
        </div>
      )}

      {view.kind === "loading" && (
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <Loader2 size={16} className="animate-spin" />
          {pick("読み込み中...", "Loading...")}
        </div>
      )}

      {view.kind === "unconfigured" && (
        <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          {pick(
            "管理者が Meta OAuth 設定(META_APP_ID / META_CONFIG_ID / META_GRAPH_VERSION)を行っていないため、連携機能は無効です。",
            "Meta OAuth is not configured by an administrator (META_APP_ID / META_CONFIG_ID / META_GRAPH_VERSION), so connection is disabled.",
          )}
        </div>
      )}

      {view.kind === "error" && (
        <div className="space-y-2">
          <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900 flex items-start gap-2">
            <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />
            <div>{view.message}</div>
          </div>
          <Button variant="secondary" onClick={handleRetry} disabled={busy !== null}>
            <RefreshCw size={16} className={`mr-2 ${busy === "load" ? "animate-spin" : ""}`} />
            {pick("再試行", "Retry")}
          </Button>
        </div>
      )}

      {(view.kind === "no_connection" || view.kind === "connected" ||
        view.kind === "reauth_required") && (
        <>
          <ConnectionStatus view={view} language={language} pick={pick} />
          <ActionButtons
            view={view}
            busy={busy}
            infoPanel={infoPanel}
            setInfoPanel={setInfoPanel}
            pick={pick}
            onConnect={handleConnect}
            onReconnect={handleReconnect}
            onDisconnectAndPurge={handleDisconnectAndPurge}
            confirmDisconnectAndPurge={confirmDisconnectAndPurge}
            setConfirmDisconnectAndPurge={setConfirmDisconnectAndPurge}
          />
        </>
      )}
    </div>
  );
}

function ConnectionStatus({
  view,
  language,
  pick,
}: {
  view: ViewState;
  language: "ja" | "en";
  pick: (ja: string, en: string) => string;
}) {
  if (view.kind === "no_connection") {
	    return (
	      <div className="meta-settings-empty">
	        <div className="meta-settings-empty-icon">
	          <Instagram size={22} />
        </div>
        <div>
          <h4>{pick("Instagram プロアカウントを連携", "Connect an Instagram professional account")}</h4>
          <p>{pick("投稿、コメント、DM をこの管理画面から扱えるようにします。", "Manage posts, comments, and DMs from this admin screen.")}</p>
        </div>
	      </div>
    );
  }
  if (view.kind !== "connected" && view.kind !== "reauth_required") {
    return null;
  }
  const conn = view.connection;
  return (
    <div className="meta-settings-product">
      <div className="meta-settings-identity">
        <div className="meta-settings-avatar">
          <Instagram size={24} />
        </div>
        <div className="meta-settings-title-block">
          <div className="meta-settings-title-line">
              <h4>Meta・Instagram</h4>
              {conn.status === "connected" ? (
                <Badge color="green" dot>{pick("接続中", "Connected")}</Badge>
              ) : conn.status === "reauth_required" ? (
                <Badge color="yellow" dot>{pick("要確認", "Needs review")}</Badge>
              ) : (
                <Badge color="gray">{conn.status}</Badge>
              )}
            </div>
            <p>{pick("投稿・コメント・DM 管理", "Posts, comments, and DM management")}</p>
        </div>
      </div>

	      <div className="meta-settings-grid">
	        <InfoTile
	          icon={<Instagram size={18} />}
	          label={pick("Instagram アカウント", "Instagram Account")}
	          value={conn.instagram_username ? `@${conn.instagram_username}` : pick("未設定", "Not set")}
	          hint={pick("連携済みアカウント", "Connected account")}
	        />
	        <InfoTile
	          icon={<Flag size={18} />}
	          label={pick("Facebook ページ", "Facebook Page")}
	          value={conn.page_name || pick("未設定", "Not set")}
	          hint={conn.business_name ? `Meta Business: ${conn.business_name}` : pick("Meta Business 未設定", "Meta Business not set")}
	        />
	        <InfoTile
	          icon={<CheckCircle2 size={18} />}
	          label={pick("最終確認", "Last checked")}
	          value={formatDateTime(conn.last_verified_at, language)}
	          hint={view.kind === "reauth_required" ? pick("再認証が必要です", "Reauthentication is required") : pick("接続状態を確認済みです", "Connection status verified")}
	        />
      </div>

	      <div className="meta-settings-permission-summary">
	        <div className="meta-settings-permission-copy">
	          <span className="meta-settings-section-label">{pick("権限の概要", "Permission Summary")}</span>
	          <strong>{pick("投稿・コメント・DM の管理が可能です", "Post, comment, and DM management is available")}</strong>
	          <p>{pick("必要な権限は付与済みです。詳細は情報ボタンから確認できます。", "Required permissions are granted. Use the info button to view details.")}</p>
        </div>
        {view.kind === "reauth_required" && view.message && (
          <div className="meta-settings-alert">
            <AlertCircle size={16} />
            <span>{view.message}</span>
          </div>
        )}
      </div>
    </div>
  );
}

function InfoTile({ icon, label, value, hint }: { icon: ReactNode; label: string; value: string; hint: string }) {
  return (
    <div className="meta-settings-info-tile">
      <div className="meta-settings-info-icon">{icon}</div>
      <div>
        <span>{label}</span>
        <strong>{value}</strong>
        <p>{hint}</p>
      </div>
    </div>
  );
}

function ActionButtons(props: {
	  view: ViewState;
	  busy: "load" | "purge" | null;
	  infoPanel: InfoPanel;
	  setInfoPanel: (panel: InfoPanel) => void;
	  pick: (ja: string, en: string) => string;
	  onConnect: () => void;
  onReconnect: () => void;
  onDisconnectAndPurge: () => Promise<void>;
  confirmDisconnectAndPurge: boolean;
  setConfirmDisconnectAndPurge: (v: boolean) => void;
}) {
  const {
	    view,
	    busy,
	    infoPanel,
	    setInfoPanel,
	    pick,
	    onConnect,
    onReconnect,
    onDisconnectAndPurge,
    confirmDisconnectAndPurge,
    setConfirmDisconnectAndPurge,
  } = props;

  if (view.kind === "no_connection") {
	    return (
	      <div className="meta-settings-actions">
	        <Button onClick={onConnect} disabled={busy !== null}>
	          <Instagram size={16} className="mr-2" /> {pick("Instagram と連携", "Connect Instagram")}
	        </Button>
      </div>
    );
  }
  if (view.kind !== "connected" && view.kind !== "reauth_required") {
    return null;
  }
  const conn = view.connection;

	  return (
	    <div className="meta-settings-action-wrap">
	      <div className="meta-settings-actions">
	        <Button variant="secondary" onClick={onReconnect} disabled={busy !== null}>
	          <ExternalLink size={16} className="mr-2" /> {pick("再認証", "Reconnect")}
	        </Button>
	        <button
	          type="button"
	          className={`meta-settings-info-button ${infoPanel === "permissions" ? "is-active" : ""}`}
	          onClick={() => setInfoPanel(infoPanel === "permissions" ? null : "permissions")}
	          aria-label={pick("権限の詳細", "Permission details")}
          aria-expanded={infoPanel === "permissions"}
        >
          <Info size={16} />
        </button>
        {!confirmDisconnectAndPurge ? (
          <div className="meta-settings-danger-group">
            <Button
              variant="secondary"
              onClick={() => setConfirmDisconnectAndPurge(true)}
	              disabled={busy !== null}
	              className="meta-settings-danger-button"
	            >
	              <Trash2 size={16} className="mr-2" /> {pick("連携解除とデータ削除", "Disconnect and Delete Data")}
	            </Button>
	            <button
	              type="button"
	              className={`meta-settings-info-button meta-settings-info-button--danger ${infoPanel === "disconnect" ? "is-active" : ""}`}
	              onClick={() => setInfoPanel(infoPanel === "disconnect" ? null : "disconnect")}
	              aria-label={pick("連携解除とデータ削除の詳細", "Disconnect and delete details")}
              aria-expanded={infoPanel === "disconnect"}
            >
              <Info size={16} />
            </button>
          </div>
        ) : (
	          <div className="meta-settings-confirm-actions">
	            <Button onClick={onDisconnectAndPurge} disabled={busy !== null} variant="danger">
	              {busy === "purge" ? pick("実行中...", "Running...") : pick("連携解除と削除を実行", "Disconnect and delete")}
	            </Button>
            <Button
              variant="secondary"
	              onClick={() => setConfirmDisconnectAndPurge(false)}
	              disabled={busy !== null}
	            >
	              {pick("キャンセル", "Cancel")}
	            </Button>
          </div>
        )}
      </div>
	      {infoPanel === "permissions" && <PermissionsInfoPanel onClose={() => setInfoPanel(null)} pick={pick} />}
	      {infoPanel === "disconnect" && <DisconnectInfoPanel onClose={() => setInfoPanel(null)} pick={pick} />}
    </div>
  );
}

function PermissionsInfoPanel({
  onClose,
  pick,
}: {
  onClose: () => void;
  pick: (ja: string, en: string) => string;
}) {
  return (
    <div className="meta-settings-popover">
      <div className="meta-settings-popover-head">
        <div>
          <h4>{pick("権限", "Permissions")}</h4>
        </div>
        <button type="button" onClick={onClose} aria-label={pick("閉じる", "Close")}>
          <X size={16} />
        </button>
      </div>
      {[
        "business_management",
        "instagram_basic",
        "instagram_content_publish",
        "instagram_manage_comments",
        "instagram_manage_contents",
        "instagram_manage_messages",
        "pages_read_engagement",
        "pages_show_list",
      ].map((scope) => (
        <PermissionItem key={scope} icon={<KeyRound size={18} />} title={scope} />
      ))}
    </div>
  );
}

function DisconnectInfoPanel({
  onClose,
  pick,
}: {
  onClose: () => void;
  pick: (ja: string, en: string) => string;
}) {
  return (
	    <div className="meta-settings-popover meta-settings-popover--danger">
	      <div className="meta-settings-popover-head">
	        <div>
	          <h4>{pick("連携解除とデータ削除", "Disconnect and Delete Data")}</h4>
	          <p>{pick("実行前に削除範囲を確認できます", "Review what will be deleted before running this action")}</p>
	        </div>
	        <button type="button" onClick={onClose} aria-label={pick("閉じる", "Close")}>
	          <X size={16} />
	        </button>
	      </div>
	      <PermissionItem icon={<KeyRound size={18} />} title={pick("削除されるもの", "What will be deleted")} description={pick("Meta API トークン、Business/Page/Instagram の連携情報、自動同期の状態を削除します。", "Deletes Meta API tokens, Business/Page/Instagram connection records, and auto-sync state.")} />
	      <PermissionItem icon={<Database size={18} />} title={pick("ローカルデータ", "Local Data")} description={pick("この操作はローカルの Instagram 連携データ削除を実行します。Instagram 上の実データは削除しません。", "This deletes local Instagram connection data only. It does not delete live data on Instagram.")} />
	      <PermissionItem icon={<Building2 size={18} />} title={pick("再利用する場合", "To Use Again")} description={pick("再度利用するには Meta 認証から連携し直してください。", "Reconnect through Meta authentication to use this integration again.")} />
    </div>
  );
}

function PermissionItem({ icon, title, description }: { icon: ReactNode; title: string; description?: string }) {
  return (
    <div className="meta-settings-permission-item">
      <div className="meta-settings-permission-icon">{icon}</div>
      <div>
	        <strong>{title}</strong>
	        {description ? <p>{description}</p> : null}
      </div>
      <CheckCircle2 size={16} className="meta-settings-permission-check" />
    </div>
  );
}
